/**
 * Floating UI of the sheet viewer: page / spread indicator, page-layout hint, paged-mode hint, first /
 * last page hints, "↩ Til baka" pill, load states. Chips at the bottom / top keep clear of the song
 * page's floating strip of open songs (CSS --tabs-bottom / --tabs-top, 0 without it).
 */
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, LoaderCircle, RefreshCw, Undo2, WifiOff } from 'lucide-react';
import { ApiFailure, NetworkFailure } from '../api/client';
import { Button, cx } from '../components/ui';
import { useT } from '../i18n';

export type LoadErrorKind = 'offline' | 'forbidden' | 'stale' | 'failed';

export function loadErrorKind(e: unknown): LoadErrorKind {
  if (e instanceof NetworkFailure) return 'offline';
  if (e instanceof ApiFailure) {
    if (e.status === 404 || e.status === 410) return 'stale';
    if (e.status === 403 || e.status === 401) return 'forbidden';
  }
  if (e instanceof TypeError && typeof navigator !== 'undefined' && !navigator.onLine) return 'offline';
  return 'failed';
}

/** "3 / 12", or "3–4 / 12" for a two-up spread (`last` > `page`). Pages are 1-based. */
export function PageIndicator({ page, last, count, busy }: { page: number; last?: number; count: number; busy: boolean }) {
  const t = useT();
  const range = last !== undefined && last > page;
  return (
    <div
      className="pointer-events-none absolute top-3 right-4 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-[15px] font-semibold tabular-nums text-white"
      aria-label={range ? t('viewer.spread_aria', { first: page, last, count }) : t('viewer.page_aria', { page, count })}
      role="status"
    >
      {busy ? <LoaderCircle className="size-4 animate-spin" aria-hidden /> : null}
      {range ? t('viewer.spread_indicator', { first: page, last, count }) : t('viewer.page_indicator', { page, count })}
    </div>
  );
}

/** The page layout a double-tap switched to: "Samfellt" or "Heil síða". */
export function FitHint({ mode }: { mode: 'scroll' | 'page' }) {
  const t = useT();
  return (
    <div
      className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-black/70 px-5 py-3 text-[18px] font-semibold text-white"
      role="status"
    >
      {mode === 'page' ? t('viewer.mode_page') : t('viewer.mode_scroll')}
    </div>
  );
}

/** One-time hint of a paged mode (Heil síða, Tvær síður): the left / right part of the screen and swipes turn. */
export function PagedHint() {
  const t = useT();
  return (
    <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 flex -translate-y-1/2 justify-center px-4">
      <div
        className="flex max-w-xl items-center gap-3 rounded-2xl bg-black/75 px-4 py-3 text-[18px] leading-snug font-semibold text-white shadow-lg"
        role="status"
        style={{ animation: 'kgr-toast-in 160ms ease-out' }}
      >
        <ChevronLeft className="size-7 shrink-0" aria-hidden />
        <span className="text-center">{t('viewer.two_up_hint')}</span>
        <ChevronRight className="size-7 shrink-0" aria-hidden />
      </div>
    </div>
  );
}

/**
 * A tap or swipe that could not turn: "Fyrsta síða" by the left edge, "Síðasta síða" by the right
 * (vertically centred, clear of the indicator, the "Næsta lag ›" chip and the back pill).
 */
export function EdgeHint({ edge }: { edge: 'first' | 'last' }) {
  const t = useT();
  const first = edge === 'first';
  return (
    <div className={cx('pointer-events-none absolute top-1/2 z-20 -translate-y-1/2', first ? 'left-4' : 'right-4')}>
      <div
        className="flex items-center gap-2 rounded-2xl bg-black/70 px-4 py-3 text-[18px] font-semibold text-white shadow-lg"
        role="status"
        style={{ animation: 'kgr-toast-in 160ms ease-out' }}
      >
        {first ? <ChevronFirst className="size-6 shrink-0" aria-hidden /> : null}
        {first ? t('viewer.first_page') : t('viewer.last_page')}
        {first ? null : <ChevronLast className="size-6 shrink-0" aria-hidden />}
      </div>
    </div>
  );
}

/**
 * "↩ Til baka" after a connector jump. `compact` (a phone held upright, see backPill.ts): the pill is drawn
 * half the size (26 px instead of 52 px tall) inside a transparent button that stays a 44 pt touch target;
 * the pill's bottom edge stays where the big one's is.
 */
export function BackPill({ onBack, compact }: { onBack: () => void; compact?: boolean }) {
  const t = useT();
  if (compact) {
    return (
      <div className="pointer-events-none absolute inset-x-0 z-30 flex justify-center" style={{ bottom: 'calc(var(--tabs-bottom, 0px) + 0.75rem)' }}>
        <button
          type="button"
          onClick={onBack}
          className="pointer-events-auto inline-flex min-h-11 items-center px-1 active:scale-95"
          style={{ animation: 'kgr-toast-in 160ms ease-out' }}
        >
          <span
            data-back-pill
            className="inline-flex h-[26px] items-center gap-1 rounded-full bg-[var(--color-navy-700)] px-2.5 text-[13px] leading-none font-semibold text-white shadow-[0_3px_12px_rgb(0_0_0/0.3)]"
          >
            <Undo2 className="size-3.5" aria-hidden />
            {t('viewer.jump_back')}
          </span>
        </button>
      </div>
    );
  }
  return (
    <div className="pointer-events-none absolute inset-x-0 z-30 flex justify-center" style={{ bottom: 'calc(var(--tabs-bottom, 0px) + 1.25rem)' }}>
      <button
        type="button"
        onClick={onBack}
        className="pointer-events-auto inline-flex min-h-13 items-center gap-2 rounded-full bg-[var(--color-navy-700)] px-6 py-3 text-[18px] font-semibold text-white shadow-[0_6px_24px_rgb(0_0_0/0.3)] active:scale-95"
        style={{ animation: 'kgr-toast-in 160ms ease-out' }}
      >
        <Undo2 className="size-6" aria-hidden />
        {t('viewer.jump_back')}
      </button>
    </div>
  );
}

export function HqErrorChip({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div
      className="absolute left-4 z-20 flex items-center gap-2 rounded-full bg-[var(--surface)] py-1 pr-1 pl-4 text-[15px] shadow-lg"
      style={{ bottom: 'calc(var(--tabs-bottom, 0px) + 1.25rem)' }}
    >
      <span>{t('viewer.hq_failed')}</span>
      <Button variant="ghost" icon={RefreshCw} onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}

export function LoadErrorOverlay({ kind, onRetry }: { kind: LoadErrorKind; onRetry: () => void }) {
  const t = useT();
  const message =
    kind === 'offline'
      ? t('viewer.load_offline')
      : kind === 'forbidden'
        ? t('viewer.load_forbidden')
        : kind === 'stale'
          ? t('viewer.rev_changed')
          : t('viewer.load_failed');
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-[var(--bg)]/70 p-6">
      <div role="alert" className="flex max-w-md flex-col items-center gap-4 rounded-3xl bg-[var(--surface)] p-6 text-center shadow-[var(--shadow-sheet)]">
        {kind === 'offline' ? <WifiOff className="size-10 text-[var(--muted)]" aria-hidden /> : null}
        {kind === 'stale' ? <LoaderCircle className="size-10 animate-spin text-[var(--muted)]" aria-hidden /> : null}
        <p className="text-[18px] font-medium">{message}</p>
        {kind === 'offline' || kind === 'failed' ? (
          <Button icon={RefreshCw} onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function LoadingPill() {
  const t = useT();
  return (
    <div className="pointer-events-none absolute inset-x-0 flex justify-center" style={{ top: 'calc(var(--tabs-top, 0px) + 3.5rem)' }}>
      <span className="inline-flex items-center gap-2 rounded-full bg-[var(--surface)] px-4 py-2 text-[16px] text-[var(--muted)] shadow">
        <LoaderCircle className="size-5 animate-spin" aria-hidden />
        {t('viewer.loading')}
      </span>
    </div>
  );
}
